import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { useAuth } from './AuthContext';
import { useAuthenticatedApi } from '@/hooks/useAuthenticatedApi';
import { showSuccessToast, showErrorToast } from '@/lib/toast-utils';

interface FavoriteItemEntry {
  listId: string;
  listName: string;
  itemId: string; // the favorite-item relationship id for DELETE
}

interface FavoriteList {
  id: string;
  name: string;
  isDefault: boolean;
  productCount: number;
}

interface FavoritesContextType {
  lists: FavoriteList[];
  listsLoading: boolean;
  isProductFavorited: (productId: string) => boolean;
  getProductFavoriteLists: (productId: string) => FavoriteItemEntry[];
  addToFavorites: (productId: string, quantity?: number) => Promise<void>;
  removeFromFavorites: (productId: string) => Promise<void>;
  clearFavoritesCache: () => void;
  /** Open the picker for add/remove when multiple lists exist */
  pickerState: PickerState | null;
  closePickerAndReset: () => void;
  confirmPickerSelection: (listId: string) => Promise<void>;
  /** Open the create-list modal from inside the picker */
  openCreateListFromPicker: boolean;
  setOpenCreateListFromPicker: (v: boolean) => void;
  handleNewListCreated: (list: { id: string; name: string }) => void;
}

type PickerMode = 'add' | 'remove';

interface PickerState {
  mode: PickerMode;
  productId: string;
  quantity: number;
}

const FavoritesContext = createContext<FavoritesContextType | undefined>(undefined);

export const useFavorites = () => {
  const ctx = useContext(FavoritesContext);
  if (!ctx) throw new Error('useFavorites must be used within FavoritesProvider');
  return ctx;
};

// Module-level session key for tracking profile changes
const PROFILE_KEY = 'selectedProfile';

export const FavoritesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { makeAuthenticatedCall } = useAuthenticatedApi();
  const queryClient = useQueryClient();

  // product → list entries map
  const [favoriteMap, setFavoriteMap] = useState<Map<string, FavoriteItemEntry[]>>(new Map());
  const [pickerState, setPickerState] = useState<PickerState | null>(null);
  const [openCreateListFromPicker, setOpenCreateListFromPicker] = useState(false);
  const profileRef = useRef<string | null>(null);

  // ─── Fetch lists ───
  const listsQuery = useQuery({
    queryKey: ['get-favorites-lists'],
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    queryFn: async () => {
      const result = await makeAuthenticatedCall<{
        success: boolean;
        favoriteLists: Array<{
          id: number;
          name: string;
          isDefault: boolean;
          productList: { totalProductsCount: number };
        }>;
        error?: string;
      }>('get-favorites-lists');
      if (result.error) throw new Error(result.error);
      if (!result.data?.success) throw new Error(result.data?.error ?? 'Failed');
      return (result.data.favoriteLists ?? []).map((l) => ({
        id: String(l.id),
        name: l.name,
        isDefault: l.isDefault,
        productCount: l.productList?.totalProductsCount ?? 0,
      }));
    },
  });

  const lists: FavoriteList[] = listsQuery.data ?? [];

  // Track which list IDs we've already fetched items for
  const fetchedListIdsRef = useRef<Set<string>>(new Set());

  // ─── Fetch all list details to build the favorites map ───
  const allListDetailsQuery = useQuery({
    queryKey: ['favorites-all-items', lists.map(l => l.id).join(',')],
    enabled: lists.length > 0 && !!user,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    queryFn: async ({ queryKey }) => {
      // Determine which lists are new (not yet fetched)
      const currentListIds = new Set(lists.map(l => l.id));
      const newListIds = lists.filter(l => !fetchedListIdsRef.current.has(l.id));
      
      // Remove stale list IDs that no longer exist
      for (const id of fetchedListIdsRef.current) {
        if (!currentListIds.has(id)) fetchedListIdsRef.current.delete(id);
      }

      // Start from existing map data (preserve already-fetched lists)
      const map = new Map<string, FavoriteItemEntry[]>(favoriteMap);
      
      // Remove entries for lists that no longer exist
      for (const [productId, entries] of map) {
        const filtered = entries.filter(e => currentListIds.has(e.listId));
        if (filtered.length === 0) map.delete(productId);
        else map.set(productId, filtered);
      }

      // Only fetch details for new lists (skip if they have 0 products)
      const listsToFetch = newListIds.filter(l => l.productCount > 0);
      
      if (listsToFetch.length > 0) {
        const results = await Promise.all(
          listsToFetch.map(async (list) => {
            try {
              const result = await makeAuthenticatedCall<{
                success: boolean;
                favoriteItems: Array<{ productId: string; favoriteItemId: string }>;
                pagination: { totalCount: number; totalPages: number };
              }>('get-favorites-list-detail', { listId: list.id, page: 1, pageSize: 1000 });
              if (result.data?.success) {
                return { listId: list.id, listName: list.name, products: result.data.favoriteItems ?? [] };
              }
            } catch {}
            return { listId: list.id, listName: list.name, products: [] };
          })
        );

        for (const r of results) {
          for (const p of r.products) {
            const existing = map.get(p.productId) ?? [];
            existing.push({ listId: r.listId, listName: r.listName, itemId: p.favoriteItemId || p.productId });
            map.set(p.productId, existing);
          }
        }
      }

      // Mark all current lists as fetched
      for (const l of lists) fetchedListIdsRef.current.add(l.id);

      return map;
    },
  });

  useEffect(() => {
    if (allListDetailsQuery.data) {
      setFavoriteMap(allListDetailsQuery.data);
    }
  }, [allListDetailsQuery.data]);

  // ─── Watch for profile changes ───
  useEffect(() => {
    const check = () => {
      const current = sessionStorage.getItem(PROFILE_KEY);
      if (profileRef.current !== null && current !== profileRef.current) {
        clearFavoritesCache();
      }
      profileRef.current = current;
    };
    check();
    window.addEventListener('storage', check);
    return () => window.removeEventListener('storage', check);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Core methods ───
  const clearFavoritesCache = useCallback(() => {
    setFavoriteMap(new Map());
    fetchedListIdsRef.current.clear();
    queryClient.invalidateQueries({ queryKey: ['get-favorites-lists'] });
    queryClient.invalidateQueries({ queryKey: ['get-favorites-list-detail'] });
    queryClient.invalidateQueries({ queryKey: ['favorites-all-items'] });
  }, [queryClient]);

  const isProductFavorited = useCallback(
    (productId: string) => favoriteMap.has(productId),
    [favoriteMap]
  );

  const getProductFavoriteLists = useCallback(
    (productId: string) => favoriteMap.get(productId) ?? [],
    [favoriteMap]
  );

  const executeAdd = useCallback(async (productId: string, listId: string, quantity: number) => {
    const result = await makeAuthenticatedCall<{ success: boolean; error?: string }>(
      'add-to-favorites',
      { listId, productId, quantity, productVariantId: '' }
    );
    if (result.error || !result.data?.success) {
      showErrorToast(result.error || result.data?.error || 'Failed to add to favorites', 'Error');
      return;
    }
    const listName = lists.find(l => l.id === listId)?.name ?? 'favorites';
    showSuccessToast(`Added to "${listName}"`, 'Added to Favorites');
    // Optimistic update for immediate UI feedback (placeholder itemId)
    setFavoriteMap(prev => {
      const next = new Map(prev);
      const existing = [...(next.get(productId) ?? [])];
      existing.push({ listId, listName, itemId: productId });
      next.set(productId, existing);
      return next;
    });

    // Re-fetch list items to get the real favoriteItemId
    try {
      const itemsResult = await makeAuthenticatedCall<{
        success: boolean;
        favoriteItems: Array<{ productId: string; favoriteItemId: string }>;
      }>('get-favorites-list-detail', { listId, page: 1, pageSize: 1000 });

      if (itemsResult.data?.success) {
        const match = itemsResult.data.favoriteItems.find(i => i.productId === productId);
        if (match) {
          setFavoriteMap(prev => {
            const next = new Map(prev);
            const entries = (next.get(productId) ?? []).map(e =>
              e.listId === listId ? { ...e, itemId: match.favoriteItemId } : e
            );
            next.set(productId, entries);
            return next;
          });
        }
      }
    } catch {} // non-critical — removal will still work after next full refresh

    queryClient.invalidateQueries({ queryKey: ['get-favorites-lists'] });
  }, [makeAuthenticatedCall, lists, queryClient]);

  const executeRemove = useCallback(async (productId: string, listId: string, itemId: string) => {
    const result = await makeAuthenticatedCall<{ success: boolean; error?: string }>(
      'remove-from-favorites',
      { listId, itemId }
    );
    if (result.error || !result.data?.success) {
      showErrorToast(result.error || result.data?.error || 'Failed to remove from favorites', 'Error');
      return;
    }
    const listName = lists.find(l => l.id === listId)?.name ?? 'favorites';
    showSuccessToast(`Removed from "${listName}"`, 'Removed from Favorites');
    // Optimistic update — don't clear entire map
    setFavoriteMap(prev => {
      const next = new Map(prev);
      const entries = (next.get(productId) ?? []).filter(e => e.listId !== listId);
      if (entries.length === 0) next.delete(productId);
      else next.set(productId, entries);
      return next;
    });
    queryClient.invalidateQueries({ queryKey: ['get-favorites-lists'] });
    queryClient.invalidateQueries({ queryKey: ['get-favorites-list-detail'] });
  }, [makeAuthenticatedCall, lists, queryClient]);

  const ensureDefaultList = useCallback(async (): Promise<string | null> => {
    const result = await makeAuthenticatedCall<{ success: boolean; id?: number; error?: string }>(
      'create-favorites-list',
      { name: 'Default', isDefault: true }
    );
    if (result.error || !result.data?.success) {
      showErrorToast(result.error || result.data?.error || 'Failed to create default list', 'Error');
      return null;
    }
    showSuccessToast('Created "Default" favorites list', 'List Created');
    await queryClient.invalidateQueries({ queryKey: ['get-favorites-lists'] });
    return String(result.data.id);
  }, [makeAuthenticatedCall, queryClient]);

  const addToFavorites = useCallback(async (productId: string, quantity = 1) => {
    if (lists.length === 0) {
      const newId = await ensureDefaultList();
      if (newId) await executeAdd(productId, newId, quantity);
      return;
    }
    if (lists.length === 1) {
      await executeAdd(productId, lists[0].id, quantity);
      return;
    }
    // Multiple lists → open picker
    setPickerState({ mode: 'add', productId, quantity });
  }, [lists, ensureDefaultList, executeAdd]);

  const removeFromFavorites = useCallback(async (productId: string) => {
    const entries = favoriteMap.get(productId);
    if (!entries || entries.length === 0) return;
    if (entries.length === 1) {
      await executeRemove(productId, entries[0].listId, entries[0].itemId);
      return;
    }
    // Multiple lists → open picker for removal
    setPickerState({ mode: 'remove', productId, quantity: 0 });
  }, [favoriteMap, executeRemove]);

  const closePickerAndReset = useCallback(() => {
    setPickerState(null);
    setOpenCreateListFromPicker(false);
  }, []);

  const confirmPickerSelection = useCallback(async (listId: string) => {
    if (!pickerState) return;
    if (pickerState.mode === 'add') {
      await executeAdd(pickerState.productId, listId, pickerState.quantity);
    } else {
      const entries = favoriteMap.get(pickerState.productId) ?? [];
      const entry = entries.find(e => e.listId === listId);
      if (entry) {
        await executeRemove(pickerState.productId, listId, entry.itemId);
      }
    }
    closePickerAndReset();
  }, [pickerState, executeAdd, executeRemove, favoriteMap, closePickerAndReset]);

  const handleNewListCreated = useCallback(async (list: { id: string; name: string }) => {
    setOpenCreateListFromPicker(false);
    if (pickerState?.mode === 'add') {
      await executeAdd(pickerState.productId, list.id, pickerState.quantity);
      closePickerAndReset();
    }
  }, [pickerState, executeAdd, closePickerAndReset]);

  return (
    <FavoritesContext.Provider
      value={{
        lists,
        listsLoading: listsQuery.isLoading,
        isProductFavorited,
        getProductFavoriteLists,
        addToFavorites,
        removeFromFavorites,
        clearFavoritesCache,
        pickerState,
        closePickerAndReset,
        confirmPickerSelection,
        openCreateListFromPicker,
        setOpenCreateListFromPicker,
        handleNewListCreated,
      }}
    >
      {children}
    </FavoritesContext.Provider>
  );
};
